import { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MuiLink from '@mui/material/Link'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  CancelIcon,
  CertificateIcon,
  ClockIcon,
  ConfirmIcon,
  EventIcon,
  ImageIcon,
  LiveClassIcon,
  LocationIcon,
  MoreHorizIcon,
  PendingIcon,
  ScheduleIcon,
  UserIcon,
  VerifiedIcon,
  WarningIcon,
} from 'src/v3/core/icons'
import { getS3ImageFilePath } from 'src/v3/utilities/image-upload'
import type {
  EventInviteStatus,
  HybridEventLocation,
  InPersonEventLocation,
  VirtualEventLocation,
} from 'src/v3/core/types'
import { accreditationDict } from 'src/v3/events/accreditations'
import {
  activeTabChanged,
  fetchSessions,
  selectAccreditationsByTypeByEvent,
  selectEventAttention,
  selectEventV2ById,
  selectInstructors,
  selectSessionById,
} from '../events-v2.slice'
import type { EventsTab } from '../types'
import { postRsvpRegister, putRsvpStatus } from '../utils/api'
import { downloadIcs } from '../utils/ics-export'
import {
  deriveRegistrationDisplayInfo,
  deriveRegistrationState,
  deriveRsvpState,
  type RegistrationInfo,
  type RsvpState,
} from '../utils/rsvp-state'
import { getRsvpColors, rsvpLabelFull } from '../utils/constants'
import { isJoinWindowOpen, isStartingSoon } from '../utils/access-window'
import { normalizeUrl, transformEventState } from '../utils/transforms'
import { attentionLabel } from '../utils/session-attention'

dayjs.extend(utc)
dayjs.extend(timezone)

let LOCAL_TZ = dayjs.tz.guess()

let iconSx = { fontSize: 14, color: 'text.disabled', flexShrink: 0 }

type IconComponent = React.ComponentType<{ sx?: object }>

let rsvpIcon: Record<RsvpState, IconComponent | null> = {
  open: EventIcon,
  invited: EventIcon,
  accepted: ConfirmIcon,
  declined: CancelIcon,
  waitlisted: PendingIcon,
  attended: VerifiedIcon,
  past: null,
}

let statusTab: Partial<Record<EventInviteStatus, EventsTab>> = {
  accepted: 'registered',
  waitlisted: 'upcoming',
  declined: 'upcoming',
  invited: 'invited',
  attended: 'attended',
}

let rsvpActionMessages: Record<EventInviteStatus, string> = {
  invited: 'Invitation updated',
  accepted: 'Accepted invitation',
  declined: 'Declined session',
  waitlisted: 'Added to waitlist',
  attended: 'Marked as attended',
}

// An outstanding CE requirement on the session, shown in place of its RSVP status.
export type SessionAttention = {
  label: string
  action?: { label: string; onClick: () => void }
}

type Props = {
  sessionId: string
  onSuccess: (msg: string) => void
  onError: (msg: string) => void
  disableCardLink?: boolean
  attention?: SessionAttention
  // Off where every row belongs to the same event, so the image would only repeat.
  showThumbnail?: boolean
}

export default function SessionRow({
  sessionId,
  onSuccess,
  onError,
  disableCardLink = false,
  attention: attentionProp,
  showThumbnail = true,
}: Props) {
  let dispatch = useAppDispatch()
  let theme = useTheme()
  let rsvpColors = getRsvpColors(theme)
  let [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  let [confirmDeclineOpen, setConfirmDeclineOpen] = useState(false)
  let [announcement, setAnnouncement] = useState('')
  let [isUpdating, setIsUpdating] = useState(false)

  let session = useAppSelector(selectSessionById(sessionId))
  let event = useAppSelector(selectEventV2ById(session?.eventID ?? ''))
  let instructors = useAppSelector(selectInstructors)
  let accreditationsByType = useAppSelector(
    selectAccreditationsByTypeByEvent(session?.eventID ?? ''),
  )
  let eventAttention = useAppSelector(
    selectEventAttention(session?.eventID ?? ''),
  )

  if (!session || !event) return null

  let invites = session.invites ?? []

  // The detail page passes a richer attention (specific step plus action); elsewhere the
  // shared event attention labels the one session it names.
  let attention: SessionAttention | null =
    attentionProp ??
    (eventAttention?.sessionId === session.id
      ? { label: attentionLabel(eventAttention) }
      : null)

  let inviteState = deriveRsvpState(invites)
  let registrationState = deriveRegistrationState(
    session.sessionTime,
    session.endTime,
    session.registrationDeadline,
  )
  let rsvpState: RsvpState = inviteState ?? registrationState
  // Availability helps someone decide whether to register. Once the user holds a seat it's
  // noise, and misleading: the API projects capacity as if the user registered now, excluding
  // their own seat, so a registered user in a full session still sees seats remaining.
  let holdsSeat = inviteState === 'accepted' || inviteState === 'attended'
  let RsvpIcon = rsvpIcon[rsvpState] ?? null

  let performRsvpAction = (
    apiCall: Promise<unknown>,
    successMsg: string,
    errorMsg: string,
    tab?: EventsTab,
  ) => {
    setIsUpdating(true)
    apiCall
      .then(() => {
        if (tab) dispatch(activeTabChanged(tab))
        setAnnouncement(successMsg)
        onSuccess(successMsg)
        return dispatch(fetchSessions())
      })
      .catch(() => {
        setAnnouncement(errorMsg)
        onError(errorMsg)
      })
      .finally(() => setIsUpdating(false))
  }

  let handleRegister = () => {
    setIsUpdating(true)
    postRsvpRegister(sessionId)
      .then((invite) => {
        let tab = statusTab[invite.status]
        if (tab) dispatch(activeTabChanged(tab))
        setAnnouncement('Registered successfully')
        onSuccess('Registered successfully')
        return dispatch(fetchSessions())
      })
      .catch(() => {
        setAnnouncement('Failed to register')
        onError('Failed to register')
      })
      .finally(() => setIsUpdating(false))
  }

  let handleStatusUpdate = (newStatus: EventInviteStatus) => {
    let invite = invites[0]
    if (!invite) return
    performRsvpAction(
      putRsvpStatus(invite.id, newStatus),
      rsvpActionMessages[newStatus],
      'Failed to update RSVP',
      statusTab[newStatus],
    )
  }

  let handleDecline = () => {
    if (rsvpState === 'accepted') {
      setConfirmDeclineOpen(true)
    } else {
      handleStatusUpdate('declined')
    }
  }

  let deadlineDayjs = session.registrationDeadline
    ? dayjs.utc(session.registrationDeadline).tz(LOCAL_TZ)
    : null
  let isDeadlinePassed =
    deadlineDayjs != null && deadlineDayjs.isBefore(dayjs())
  let isDeadlineSoon =
    deadlineDayjs != null &&
    deadlineDayjs.diff(dayjs(), 'hour', true) <= 48 &&
    deadlineDayjs.isAfter(dayjs())

  let registrationInfo = session.registrationInfo ?? { state: 'open' as const }
  let effectiveRegistrationInfo: RegistrationInfo =
    registrationState === 'past' ? { state: 'closed' } : registrationInfo
  let displayInfo = deriveRegistrationDisplayInfo(
    effectiveRegistrationInfo,
    isDeadlinePassed ? null : deadlineDayjs,
  )
  let registerLabel =
    registrationInfo.state === 'waitlisted' ? 'Join Waitlist' : 'Register'

  let rsvpActions: { label: string; onClick: () => void }[] =
    registrationInfo.state === 'closed'
      ? {
          open: [],
          invited: [{ label: 'Decline', onClick: handleDecline }],
          accepted: [{ label: 'Decline', onClick: handleDecline }],
          declined: [],
          waitlisted: [{ label: 'Leave Waitlist', onClick: handleDecline }],
          attended: [],
          past: [],
        }[rsvpState]
      : {
          open: [{ label: registerLabel, onClick: handleRegister }],
          invited: [
            { label: 'Accept', onClick: () => handleStatusUpdate('accepted') },
            { label: 'Decline', onClick: handleDecline },
          ],
          accepted: [{ label: 'Decline', onClick: handleDecline }],
          declined: [
            {
              label: registerLabel,
              onClick: () =>
                handleStatusUpdate(
                  registrationInfo.state === 'waitlisted'
                    ? 'waitlisted'
                    : 'accepted',
                ),
            },
          ],
          waitlisted: [{ label: 'Leave Waitlist', onClick: handleDecline }],
          attended: [],
          past: [],
        }[rsvpState]

  let menuActions = [
    ...rsvpActions,
    { label: 'Add to calendar', onClick: () => downloadIcs(session, event) },
  ]

  let displayTime = dayjs.utc(session.sessionTime).tz(LOCAL_TZ)
  let endTimeDayjs = session.endTime
    ? dayjs.utc(session.endTime).tz(LOCAL_TZ)
    : null
  let tzAbbr = displayTime.format('z')

  let timeDisplay = endTimeDayjs
    ? `${displayTime.format('h:mm A')} – ${endTimeDayjs.format('h:mm A')} ${tzAbbr}`
    : `${displayTime.format('h:mm A')} ${tzAbbr}`

  let startingSoon = isStartingSoon(session.sessionTime)

  let instructorIDs = session.instructorIDs?.length
    ? session.instructorIDs
    : (event.instructorIDs ?? [])
  let sessionInstructors = instructors.filter((i) =>
    instructorIDs.includes(i.id),
  )
  let instructorNames = sessionInstructors.map((i) => i.name).join(', ')

  let ceBody = Object.entries(accreditationsByType)
    .filter(([, items]) => (items?.length ?? 0) > 0)
    .map(([kind]) => accreditationDict[kind as keyof typeof accreditationDict])
    .filter(Boolean)
    .slice(0, 1)
    .join('')

  let ceLabel = event.hours
    ? ceBody
      ? `${event.hours} hr · ${ceBody}`
      : `${event.hours} hr`
    : ceBody || null

  let handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setMenuAnchor(e.currentTarget)
  }

  let handleMenuClose = () => {
    setMenuAnchor(null)
  }

  let { location } = event
  let showAddress = location.kind === 'inPerson' || location.kind === 'hybrid'
  let showVirtualLink =
    location.kind === 'virtual' || location.kind === 'hybrid'

  let sessionTitle = session.name || event.name
  let { thumbnail } = event

  return (
    <>
      <Box className="relative mb-4">
        <Box
          className="relative grid overflow-hidden rounded"
          sx={{
            gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            ...(!disableCardLink && { '&:hover': { bgcolor: 'action.hover' } }),
            '&:focus-within': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
        >
          {/* Full-card link overlay — avoids nesting <a> inside <a> */}
          {!disableCardLink && (
            <Link
              to={`/events/v2/${session.eventID}`}
              aria-label={`${sessionTitle} – ${displayTime.format('MMM D, YYYY')}`}
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                textDecoration: 'none',
                outline: 'none',
              }}
            />
          )}
          {/* Date rail: the date badge sits in the same top-left spot on every row; the thumbnail
              (or a placeholder icon) fills the rail behind it, cropped by object-fit. On xs the
              rail is a strip: thumbnail, then date. Responsive values use MUI breakpoints to switch with
              gridTemplateColumns. */}
          <Box
            className="relative flex"
            sx={{
              bgcolor: 'background.paperDark',
              borderStyle: 'solid',
              borderColor: 'divider',
              borderWidth: { xs: '0 0 1px 0', sm: '0 1px 0 0' },
            }}
          >
            {showThumbnail && (
              <Box
                className="overflow-hidden shrink-0"
                sx={{
                  position: { xs: 'relative', sm: 'absolute' },
                  inset: { sm: 0 },
                  width: { xs: 112, sm: 'auto' },
                  aspectRatio: { xs: '16/9', sm: 'auto' },
                }}
              >
                {thumbnail ? (
                  <img
                    src={getS3ImageFilePath(thumbnail)}
                    alt=""
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box className="absolute inset-0 flex items-center justify-center">
                    <ImageIcon
                      sx={{ fontSize: 28, color: 'action.disabled' }}
                    />
                  </Box>
                )}
              </Box>
            )}
            <Box
              className="self-start rounded px-1.5 py-0.5 text-center"
              sx={{
                position: { sm: 'absolute' },
                top: { sm: 6 },
                left: { sm: 6 },
                m: { xs: 1, sm: 0 },
                bgcolor: 'background.paperDark',
              }}
            >
              <Typography
                className="uppercase"
                sx={{
                  fontSize: 11,
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  lineHeight: 1.2,
                }}
              >
                {displayTime.format('ddd · MMM')}
              </Typography>
              <Typography
                sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.1 }}
              >
                {displayTime.format('D')}
              </Typography>
            </Box>
          </Box>

          {/* Content column */}
          <Box className="min-w-0 py-3 pl-3" sx={{ pr: '52px' }}>
            {/* Status band: event name · timing · RSVP status */}
            <Box
              className="flex items-center gap-2 overflow-hidden"
              sx={{ mb: 0.75 }}
            >
              <Typography
                className="min-w-0 uppercase"
                noWrap
                sx={{
                  fontSize: 11,
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  flex: 1,
                }}
              >
                {event.name}
              </Typography>
              {startingSoon && (
                <Typography
                  className="shrink-0"
                  sx={{ fontSize: 11, color: 'secondary.main' }}
                >
                  Starting soon
                </Typography>
              )}
              <Box className="flex items-center gap-1 shrink-0">
                {isUpdating ? (
                  <CircularProgress size={13} color="primary" />
                ) : attention ? (
                  <>
                    <WarningIcon sx={{ fontSize: 13, color: 'warning.main' }} />
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'warning.main',
                      }}
                    >
                      {attention.label}
                    </Typography>
                  </>
                ) : (
                  <>
                    {RsvpIcon && inviteState != null && (
                      <RsvpIcon
                        sx={{ fontSize: 13, color: rsvpColors[rsvpState] }}
                      />
                    )}
                    {inviteState != null && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: rsvpColors[rsvpState],
                        }}
                      >
                        {rsvpLabelFull[rsvpState]}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {/* Title */}
            <Typography
              sx={{ fontSize: 18, fontWeight: 500, lineHeight: 1.3, mb: 0.5 }}
            >
              {sessionTitle}
            </Typography>

            {/* Meta line */}
            <Box
              className="flex flex-wrap items-center gap-3"
              sx={{ mb: 0.75 }}
            >
              <Box className="flex items-center gap-1">
                <ClockIcon sx={iconSx} />
                <Typography
                  component="span"
                  sx={{ fontSize: 13 }}
                  color="text.secondary"
                >
                  {timeDisplay}
                </Typography>
              </Box>
              {instructorNames && (
                <Box className="flex items-center gap-1">
                  <UserIcon sx={iconSx} />
                  <Typography
                    component="span"
                    sx={{ fontSize: 13 }}
                    color="text.secondary"
                  >
                    {instructorNames}
                  </Typography>
                </Box>
              )}
              {ceLabel && (
                <Box className="flex items-center gap-1">
                  <CertificateIcon sx={iconSx} />
                  <Typography
                    component="span"
                    sx={{ fontSize: 13 }}
                    color="text.secondary"
                  >
                    {ceLabel}
                  </Typography>
                </Box>
              )}
            </Box>

            {attention?.action && (
              <Button
                variant="contained"
                color="warning"
                size="small"
                onClick={attention.action.onClick}
                className="mb-1.5"
                sx={{ position: 'relative', zIndex: 2 }}
              >
                {attention.action.label}
              </Button>
            )}

            {!holdsSeat && (
              <Box className="flex items-center gap-1" sx={{ mb: 0.5 }}>
                {displayInfo.tag === 'closed' && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 13, color: 'text.secondary' }}
                  >
                    Registration closed
                  </Typography>
                )}
                {displayInfo.tag === 'waitlisted' && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'warning.main',
                    }}
                  >
                    At capacity · Waitlist available
                  </Typography>
                )}
                {displayInfo.tag === 'open' && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 13, color: 'text.secondary' }}
                  >
                    {'Registration open'}
                    {displayInfo.capacity?.basis === 'generic' &&
                      ` · ${displayInfo.capacity.remaining} seats remaining`}
                    {displayInfo.capacity?.basis === 'license' &&
                      ` · ${displayInfo.capacity.remaining} ${displayInfo.capacity.licenseType} seats remaining`}
                  </Typography>
                )}
              </Box>
            )}
            {!holdsSeat &&
              displayInfo.tag !== 'closed' &&
              displayInfo.deadline != null && (
                <Box className="flex items-center gap-1" sx={{ mb: 0.5 }}>
                  <ScheduleIcon
                    sx={{
                      ...iconSx,
                      color: isDeadlineSoon ? 'warning.main' : iconSx.color,
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 13,
                      color: isDeadlineSoon ? 'warning.main' : 'text.secondary',
                    }}
                  >
                    Register by{' '}
                    {displayInfo.deadline.format('MMM D · h:mm A z')}
                  </Typography>
                </Box>
              )}

            {/* Access block */}
            {showAddress &&
              (() => {
                let loc = location as
                  InPersonEventLocation | HybridEventLocation
                return loc.address ? (
                  <Box className="flex items-start gap-1" sx={{ mb: 0.5 }}>
                    <LocationIcon sx={{ ...iconSx, mt: '1px' }} />
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {[
                        loc.address,
                        loc.city,
                        [transformEventState(loc.state), loc.zip]
                          .filter(Boolean)
                          .join(' '),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                ) : null
              })()}
            {showVirtualLink && (
              <Box className="flex items-center gap-1">
                <LiveClassIcon sx={iconSx} />
                {rsvpState !== 'accepted' ? (
                  <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                    Virtual session
                  </Typography>
                ) : (location as VirtualEventLocation | HybridEventLocation)
                    .url &&
                  isJoinWindowOpen(session.sessionTime, session.endTime) ? (
                  <MuiLink
                    href={normalizeUrl(
                      (location as VirtualEventLocation | HybridEventLocation)
                        .url!,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ position: 'relative', zIndex: 2, fontSize: 12 }}
                  >
                    Join session
                  </MuiLink>
                ) : (
                  <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                    Link will appear here 10 minutes prior to session start
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Action menu */}
        <>
          <IconButton
            onClick={handleMenuOpen}
            disabled={isUpdating || registrationState === 'past'}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            aria-controls={`session-menu-${sessionId}`}
            aria-label={`Actions for ${sessionTitle}`}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 10,
              zIndex: 2,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
          <Menu
            id={`session-menu-${sessionId}`}
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            {menuActions.map((action) => (
              <MenuItem
                key={action.label}
                onClick={(e) => {
                  e.stopPropagation()
                  action.onClick()
                  handleMenuClose()
                }}
              >
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        </>
        {/* Screen-reader RSVP announcement */}
        <Box aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </Box>
      </Box>

      <Dialog
        open={confirmDeclineOpen}
        onClose={() => setConfirmDeclineOpen(false)}
        aria-labelledby={`decline-dialog-title-${sessionId}`}
      >
        <DialogTitle id={`decline-dialog-title-${sessionId}`}>
          Decline this session?
        </DialogTitle>
        <DialogContent>
          <Typography>
            You are currently registered. Are you sure you want to decline?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeclineOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmDeclineOpen(false)
              handleStatusUpdate('declined')
            }}
          >
            Decline
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
